import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Briefcase, ShieldCheck, Search, FileSearch, Users, Scale } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PageShell } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import servicesHero from "@/assets/services-hero.jpg";

export const Route = createFileRoute("/services_/corporate-investigation")({
  head: () => ({
    meta: [
      { title: "Corporate Investigation — Comm42 SA" },
      { name: "description", content: "Corporate investigations: due diligence, internal fraud, asset tracing, competitive intelligence and reputational risk for companies and executives." },
      { property: "og:title", content: "Corporate Investigation — Comm42 SA" },
      { property: "og:description", content: "Discreet corporate investigations: due diligence, internal fraud, asset tracing and competitive intelligence." },
      { property: "og:url", content: "https://comm42.ch/services/corporate-investigation" },
      { property: "og:type", content: "website" },
    ],
    links: [
      { rel: "canonical", href: "https://comm42.ch/services/corporate-investigation" },
    ],
  }),
  component: Page,
});

function Page() {
  const { i18n } = useTranslation();
  const it = i18n.language?.toLowerCase().startsWith("it");

  const copy = it
    ? {
        eyebrow: "CORPORATE INVESTIGATION",
        title: "Indagini aziendali riservate, condotte da professionisti.",
        subtitle:
          "Proteggiamo aziende, manager e investitori con indagini riservate, due diligence approfondite e analisi del rischio reputazionale — in Svizzera e a livello internazionale.",
        intro:
          "Le nostre Corporate Investigations affrontano le situazioni più sensibili: frodi interne, infedeltà di collaboratori, sottrazione di know-how, contenziosi internazionali e verifiche su partner commerciali. Operiamo con discrezione assoluta, metodologie forensi e una rete di consulenti legali in Europa, Americhe e Asia.",
        servicesTitle: "Cosa includono le nostre indagini aziendali",
        items: [
          { Icon: Search, t: "Due Diligence", d: "Verifiche approfondite su persone giuridiche e fisiche, beneficial owner, esposizione politica e media negativi." },
          { Icon: ShieldCheck, t: "Frodi interne", d: "Indagini su sottrazione di fondi, conflitti di interesse e violazioni dei codici di condotta interni." },
          { Icon: FileSearch, t: "Asset tracing", d: "Identificazione e tracciamento di patrimoni occultati, partecipazioni e flussi finanziari transfrontalieri." },
          { Icon: Users, t: "Competitive intelligence", d: "Analisi di mercato, monitoraggio competitor e raccolta di informazioni open source legalmente acquisite." },
          { Icon: Briefcase, t: "Background screening", d: "Verifiche su dirigenti, candidati senior, fornitori strategici e nuovi partner commerciali." },
          { Icon: Scale, t: "Supporto al contenzioso", d: "Raccolta e formalizzazione di evidenze utilizzabili in sede giudiziaria, in coordinamento con i legali del cliente." },
        ],
        ctaTitle: "Hai bisogno di un'indagine riservata?",
        ctaDesc: "Parla con il nostro team per una valutazione confidenziale e senza impegno.",
        cta: "CONTATTACI",
        back: "← Torna ai servizi",
      }
    : {
        eyebrow: "CORPORATE INVESTIGATION",
        title: "Confidential corporate investigations, run by professionals.",
        subtitle:
          "We protect companies, executives and investors with discreet investigations, in-depth due diligence and reputational risk analysis — in Switzerland and worldwide.",
        intro:
          "Our Corporate Investigations practice handles the most sensitive matters: internal fraud, employee misconduct, IP theft, cross-border disputes and counterparty verification. We operate with absolute discretion, forensic methodologies and a network of legal advisors across Europe, the Americas and Asia.",
        servicesTitle: "What our corporate investigations cover",
        items: [
          { Icon: Search, t: "Due Diligence", d: "In-depth checks on entities and individuals, beneficial owners, political exposure and adverse media." },
          { Icon: ShieldCheck, t: "Internal fraud", d: "Investigations into embezzlement, conflicts of interest and breaches of internal codes of conduct." },
          { Icon: FileSearch, t: "Asset tracing", d: "Identification and tracing of hidden assets, holdings and cross-border financial flows." },
          { Icon: Users, t: "Competitive intelligence", d: "Market analysis, competitor monitoring and lawfully acquired open-source intelligence." },
          { Icon: Briefcase, t: "Background screening", d: "Vetting of executives, senior candidates, strategic suppliers and new business partners." },
          { Icon: Scale, t: "Litigation support", d: "Collection and formalization of court-admissible evidence, coordinated with the client's legal team." },
        ],
        ctaTitle: "Need a confidential investigation?",
        ctaDesc: "Speak with our team for a confidential, no-commitment assessment.",
        cta: "CONTACT US",
        back: "← Back to services",
      };

  return (
    <PageShell>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border/40 bg-card pt-40 pb-24">
        <img
          src={servicesHero}
          alt="Comm42 corporate investigation"
          width={1920}
          height={1080}
          className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-card/70 via-card/60 to-card" />
        <div className="absolute inset-0 opacity-40 [background-image:radial-gradient(circle_at_30%_20%,oklch(0.6_0.15_240/0.5),transparent_50%),radial-gradient(circle_at_80%_80%,oklch(0.85_0.12_85/0.25),transparent_50%)]" />
        <div className="relative mx-auto max-w-5xl px-6 text-center">
          <p className="reveal-up text-xs tracking-[0.4em] text-primary">{copy.eyebrow}</p>
          <h1 className="reveal-up mt-6 font-serif text-5xl md:text-6xl" style={{ animationDelay: "0.15s" }}>
            {copy.title}
          </h1>
          <p className="reveal-up mx-auto mt-6 max-w-2xl text-lg text-muted-foreground" style={{ animationDelay: "0.3s" }}>
            {copy.subtitle}
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="bg-background py-20">
        <Reveal className="mx-auto max-w-3xl px-6 text-center">
          <p className="text-lg leading-relaxed text-muted-foreground">{copy.intro}</p>
        </Reveal>
      </section>

      {/* Services grid */}
      <section className="bg-card/30 py-20">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <h2 className="mb-12 text-center font-serif text-3xl md:text-4xl">{copy.servicesTitle}</h2>
          </Reveal>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {copy.items.map(({ Icon, t, d }, i) => (
              <Reveal key={t} delay={i * 80}>
                <div className="tilt-card group relative h-full overflow-hidden border border-border/40 bg-card/60 p-10 transition-colors hover:border-primary/60">
                  <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-primary/10 opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100" />
                  <div className="relative inline-flex h-12 w-12 items-center justify-center border border-primary/30 bg-primary/10 transition-all duration-500 group-hover:rotate-6 group-hover:border-primary">
                    <Icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="relative mt-6 font-serif text-2xl">{t}</h3>
                  <p className="relative mt-3 text-sm text-muted-foreground">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden border-y border-border/40 bg-card py-24">
        <div className="absolute inset-0 opacity-20 [background-image:radial-gradient(circle_at_50%_50%,oklch(0.85_0.12_85),transparent_70%)]" />
        <Reveal className="relative mx-auto max-w-3xl px-6 text-center">
          <h2 className="font-serif text-4xl md:text-5xl">{copy.ctaTitle}</h2>
          <p className="mt-6 text-muted-foreground">{copy.ctaDesc}</p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              className="group inline-flex items-center gap-2 bg-primary px-10 py-4 text-xs tracking-[0.3em] text-primary-foreground transition-all hover:scale-105 hover:shadow-[0_30px_60px_-15px_oklch(0.5_0.09_235/0.6)]"
            >
              {copy.cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 border border-primary px-10 py-4 text-xs tracking-[0.3em] text-primary transition-all hover:bg-primary hover:text-primary-foreground"
            >
              {copy.back}
            </Link>
          </div>
        </Reveal>
      </section>
    </PageShell>
  );
}